import Banner from 'components/GlobalAlertBanner/Banner';
import useGlobalAlerts from 'hooks/useGlobalAlerts';
import { prefersReducedMotion } from 'libs/mediaQuery';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const CLOSE_MS = 220;

const GlobalAlertBanner: FunctionComponent = () => {
  const { alerts, onDismiss, onCtaClick } = useGlobalAlerts();
  const ref = useRef<HTMLDivElement>(null);
  const [closingId, setClosingId] = useState<number | null>(null);

  // Banner height for the fixed mobile-menu offset.
  useEffect(() => {
    const root = document.documentElement;
    const update = () =>
      root.style.setProperty('--global-alert-height', `${ref.current?.offsetHeight ?? 0}px`);

    update();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      if (ref.current) {
        observer.observe(ref.current);
      }
    } else {
      window.addEventListener('resize', update);
      window.addEventListener('orientationchange', update);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      root.style.setProperty('--global-alert-height', '0px');
    };
  }, [alerts.length]);

  if (!alerts.length) {
    return null;
  }

  // One at a time; the next surfaces after dismiss.
  const [alert] = alerts;

  const handleDismiss = (id: number) => {
    if (closingId !== null) return;
    if (prefersReducedMotion()) {
      onDismiss(id);
      return;
    }
    setClosingId(id);
    window.setTimeout(() => {
      onDismiss(id);
      setClosingId(null);
    }, CLOSE_MS);
  };

  return (
    <Collapser ref={ref} $closing={closingId === alert.id}>
      <CollapserInner>
        <Banner alert={alert} onDismiss={handleDismiss} onCtaClick={onCtaClick} />
      </CollapserInner>
    </Collapser>
  );
};

export default GlobalAlertBanner;

const Collapser = styled.div<{ $closing: boolean }>`
  display: grid;
  grid-template-rows: ${({ $closing }) => ($closing ? '0fr' : '1fr')};
  opacity: ${({ $closing }) => ($closing ? 0 : 1)};
  transition: grid-template-rows ${CLOSE_MS}ms ease, opacity ${CLOSE_MS}ms ease;
`;

const CollapserInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;
